import { parseSecretManagerField } from "@p0security/integrations-vaulted-credential-shared";
import { assertNever } from "@p0security/util/assertNever";

import type { TailscaleCredentialConfig } from "./types.ts";

/** Where an OAuth client's secret is, in the customer's own secret manager:
 * the pointer P0 gives the connector on every call, as the connector's
 * `secretPointer` schema (p0-connector-tailscale) takes it. P0 never holds
 * the secret. */
export type ClientSecretPointer =
  | { manager: "aws"; region: string; secretId: string }
  | { manager: "gcp"; projectId: string; secretId: string };

/** The Tailscale credential the connector uses, as its `credential` schema
 * takes it. */
export type ConnectorCredential =
  | { clientId: string; kind: "federated" }
  | { clientId: string; kind: "vault"; secret: ClientSecretPointer };

type OAuthClientConfig = Extract<
  TailscaleCredentialConfig,
  { type: "oauth-client" }
>;

/**
 * The partial ARN of the AWS Secrets Manager secret `name`, in `accountId` and
 * `region`: the secret's ARN without the hyphen and six random characters that
 * Secrets Manager adds to its name.
 *
 * Secrets Manager finds a secret by its partial ARN from another account too,
 * which a bare name doesn't allow. The tailnet's Terraform grants read on
 * it followed by `-??????`. It trims each value, as P0 stores it. P0 takes
 * only regions in AWS's commercial partition, so the partition is `aws`.
 *
 * It copies Auth0's helper (app#7529 @ 3c8d887b9a) instead of sharing it, so
 * adding Tailscale changes no other integration.
 */
const toSecretPartialArn = ({
  accountId,
  name,
  region,
}: {
  accountId: string;
  name: string;
  region: string;
}) =>
  `arn:aws:secretsmanager:${region.trim()}:${accountId.trim()}:secret:${name.trim()}`;

/**
 * Points the connector at the secret that holds an OAuth client's secret: by
 * its partial ARN on AWS, and by its name and project on Google Cloud.
 *
 * It trims each value, as P0 stores it, because install checks see the item
 * as the request left it.
 */
export const toClientSecretPointer = ({
  clientSecretName,
  vault,
}: Pick<
  OAuthClientConfig,
  "clientSecretName" | "vault"
>): ClientSecretPointer => {
  const locator = parseSecretManagerField(vault);
  const name = clientSecretName.trim();
  switch (locator.provider) {
    case "aws":
      return {
        manager: "aws",
        region: locator.region.trim(),
        secretId: toSecretPartialArn({
          accountId: locator.accountId,
          name,
          region: locator.region,
        }),
      };
    case "gcp":
      return {
        manager: "gcp",
        projectId: locator.projectId.trim(),
        secretId: name,
      };
    default:
      throw assertNever(locator);
  }
};

/** The connector's credential for a tailnet's stored one. A federated
 * identity needs only its client ID, from which the connector derives the
 * audience. An OAuth client also needs where its secret is. */
export const toConnectorCredential = (
  credential: TailscaleCredentialConfig
): ConnectorCredential => {
  switch (credential.type) {
    case "federated":
      return { clientId: credential.clientId.trim(), kind: "federated" };
    case "oauth-client":
      return {
        clientId: credential.clientId.trim(),
        kind: "vault",
        secret: toClientSecretPointer(credential),
      };
    default:
      throw assertNever(credential);
  }
};
